import { Typography, type TypographyProps } from '@mui/material'
import React, { useMemo } from 'react'

import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import { NODE_TEXT_MAX_WIDTH } from 'components/scenarios/concurrent/concurrent-scenario-form-utils'
import {
  getExternalActionTargetPrefix,
  getExternalActionText
} from 'components/scenarios/utils/graph/messaging'
import {
  isAWSExternalActionRequest,
  isExternalActionNode,
  isRollbackExternalActionNode
} from 'components/scenarios/utils/graph/nodes/typeguards'
import { ExternalActionPreviewSentinels } from 'types/api/reliability-tests/preview'
import {
  type ExternalActionNode,
  type ExternalActionRequest,
  ExternalActionRequestType,
  type RollbackExternalActionNode
} from 'types/api/scenarios/scenario'
import {
  getAWSAutoscalingGroupNameFromASGArn,
  getAWSLoadBalancerNameFromLoadBalancerArn
} from 'utils/status-checks/status-check-integrations-util-aws'

const TYPOGRAPHY_SPAN_PROPS = { component: 'span', variant: 'inherit' } as const

const externalActionSentinelValues: Set<string> = new Set(
  Object.values(ExternalActionPreviewSentinels)
)

function getResourceNameFromExternalActionRequest(
  externalActionRequest: ExternalActionRequest
) {
  if (isAWSExternalActionRequest(externalActionRequest)) {
    const { resourceArn, type } = externalActionRequest

    if (externalActionSentinelValues.has(resourceArn)) {
      return resourceArn
    }

    return type === ExternalActionRequestType.RemoveAWSSecurityGroups
      ? getAWSLoadBalancerNameFromLoadBalancerArn(resourceArn)
      : getAWSAutoscalingGroupNameFromASGArn(resourceArn)
  }

  return null
}

export const ExternalActionDescription = ({
  node,
  shouldDisplayResourceName = true,
  ...typographyWrapperProps
}: {
  node: ExternalActionNode | RollbackExternalActionNode
  shouldDisplayResourceName?: boolean
} & Omit<TypographyProps, 'children'>) => {
  const { externalActionRequest } = node

  const isDisablingAction =
    isExternalActionNode(node) &&
    (externalActionRequest.type ===
      ExternalActionRequestType.DisableAWSAutoScalingGroups ||
      externalActionRequest.type ===
        ExternalActionRequestType.RemoveAWSSecurityGroups)

  const actionColor = isDisablingAction ? 'error' : 'success.main'

  const { action, target, resourceName } = useMemo(() => {
    const resourceName = getResourceNameFromExternalActionRequest(
      externalActionRequest
    )

    return {
      action: isRollbackExternalActionNode(node)
        ? 'Re-enable'
        : getExternalActionText(externalActionRequest),
      target: getExternalActionTargetPrefix(
        externalActionRequest,
        shouldDisplayResourceName
      ),
      resourceName
    }
  }, [externalActionRequest, node, shouldDisplayResourceName])

  return (
    <Typography
      noWrap
      sx={{
        maxWidth: NODE_TEXT_MAX_WIDTH
      }}
      variant="body1"
      {...typographyWrapperProps}
    >
      <Typography
        color={actionColor}
        fontWeight="bold"
        {...TYPOGRAPHY_SPAN_PROPS}
      >
        {action}
      </Typography>{' '}
      <Typography {...TYPOGRAPHY_SPAN_PROPS}>
        {target}{' '}
        {shouldDisplayResourceName && (
          <TruncatedTextWithTooltip
            fontWeight="bold"
            tooltipProps={{
              title: resourceName
            }}
            {...TYPOGRAPHY_SPAN_PROPS}
          >
            {resourceName}
          </TruncatedTextWithTooltip>
        )}
      </Typography>
    </Typography>
  )
}
